/* eslint-disable react/no-danger */
import dynamic from 'next/dynamic';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { I_Media, I_Product } from '@containers/Home/types';
import { SideBySideMagnifier } from 'react-image-magnifiers';
import DangerouslySetHtmlContent from '@components/DangerouslySetHtmlContent';
import { ArrowLeft, ArrowRight } from '@components/ArrowSlide';
import { DialogHotspotProductWrapper } from './styled';
import { loadScriptPaypal } from './helper';

const DialogContainer = dynamic(() => import('@components/DialogContainer'));
const Fancybox = dynamic(() => import('./Fancybox'));

interface Props {
  product: I_Product;
  visible: boolean;
  onCancel: () => void;
}

export const DialogProduct = ({ visible, onCancel, product }: Props) => {
  const [imageActive, setImageActive] = useState<I_Media | undefined>(
    undefined,
  );
  const [initSlideDialogGalleries, setInitSlideDialogGalleries] = useState(0);
  const [visibleDialogGalleries, setVisibleDialogGalleries] = useState(false);

  const currentIndex = useMemo(
    () => product.gallery.findIndex(item => item.id === imageActive?.id),
    [imageActive?.id, product.gallery],
  );
  const htmlPaypal = useMemo(
    () => loadScriptPaypal(product.buttonType, product.button || ''),
    [product.button, product.buttonType],
  );
  const previewes = useMemo(() => {
    if (product.gallery.length > 4) {
      const galleries = [...product.gallery];
      galleries.length = 4;
      return galleries;
    }
    return product.gallery;
  }, [product.gallery]);
  const galleriesMoreThanFour = useMemo(
    () =>
      product.gallery.length > 4
        ? { item: product.gallery[4], rest: product.gallery.length - 4 }
        : { item: undefined, rest: product.gallery.length - 4 },
    [product.gallery],
  );

  useEffect(() => {
    if (!imageActive && product.gallery.length)
      setImageActive(product.gallery[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product]);

  const onChangeImageActive = useCallback(image => {
    setImageActive(image);
  }, []);

  const onOpenDialogGalleries = useCallback(
    (_e, isSeeMore = false) => {
      let index = 4;
      if (!isSeeMore) index = currentIndex;
      setInitSlideDialogGalleries(index);
      setVisibleDialogGalleries(true);
    },
    [currentIndex],
  );

  const onCloseDialogGalleries = useCallback(
    () => setVisibleDialogGalleries(false),
    [],
  );

  const onPrevImage = useCallback(() => {
    const index =
      currentIndex === 0 ? product.gallery.length - 1 : currentIndex - 1;
    setImageActive(product.gallery[index]);
  }, [currentIndex, product.gallery]);
  const onNextImage = useCallback(() => {
    const index =
      currentIndex + 1 === product.gallery.length ? 0 : currentIndex + 1;
    setImageActive(product.gallery[index]);
  }, [currentIndex, product.gallery]);

  return (
    <DialogContainer
      visible={visible}
      onCancel={onCancel}
      maskClosable
      centered
      width="800px"
      bodyStyle={{ maxWidth: '800px', width: '100%' }}
    >
      {product.buttonType === 'shopify' && product.button ? (
        <DialogHotspotProductWrapper isHeightAuto>
          <DangerouslySetHtmlContent
            className="shopify-embed"
            html={htmlPaypal}
          />
        </DialogHotspotProductWrapper>
      ) : (
        <DialogHotspotProductWrapper>
          <div className="galleries">
            <div className="image" onClick={onOpenDialogGalleries}>
              <SideBySideMagnifier
                className="input-position"
                style={{ order: '1' }}
                imageSrc={
                  imageActive?.src ||
                  'https://dummyimage.com/300x300/000/fff.png'
                }
                largeImageSrc={
                  imageActive?.src ||
                  'https://dummyimage.com/300x300/000/fff.png'
                }
                alwaysInPlace
                overlayOpacity={0.25}
                switchSides
                zoomPosition="right"
                fillAlignTop
                fillGapTop={10}
                fillGapRight={10}
                fillGapBottom={10}
                fillGapLeft={10}
              />
              <ArrowLeft
                onClick={e => {
                  e.stopPropagation();
                  onPrevImage();
                }}
              />
              <ArrowRight
                onClick={e => {
                  onNextImage();
                  e.stopPropagation();
                }}
              />
            </div>
            <div className="preview">
              {previewes?.map(item => (
                <div
                  key={item.id}
                  className={`preview-item ${
                    item.id === imageActive?.id ? 'active' : ''
                  }`}
                  style={{
                    backgroundImage: `url('${
                      item?.src_thumb ||
                      item?.src ||
                      'https://dummyimage.com/300x300/000/fff.png'
                    }')`,
                  }}
                  onClick={() => onChangeImageActive(item)}
                />
              ))}
              {galleriesMoreThanFour.rest > 0 && (
                <div
                  className="preview-item more"
                  style={{
                    backgroundImage: `url('${
                      galleriesMoreThanFour.item?.src_thumb ||
                      galleriesMoreThanFour.item?.src ||
                      'https://dummyimage.com/300x300/000/fff.png'
                    }')`,
                  }}
                  onClick={() => onOpenDialogGalleries(true)}
                >
                  <div className="overlay">
                    +{galleriesMoreThanFour?.rest} more images
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="content">
            <div className="--top">
              <h1 className="title">{product.name}</h1>
              <div className="price">
                {product.price} {product.currency || ''}
              </div>
            </div>
            <div className="--center description ctn__html sun-editor sun-editor-editable hide-scrollbar">
              <div
                dangerouslySetInnerHTML={{ __html: product.description || '' }}
              />
            </div>
            <div className="--bottom action">
              {product.buttonType === 'link' ? (
                <a
                  className="btn"
                  target="_blank"
                  href={product.button}
                  rel="noreferrer"
                  style={{ backgroundColor: product.buttonColor || '#2ec26a' }}
                >
                  {product.buttonText || 'Checkout'}
                </a>
              ) : (
                <DangerouslySetHtmlContent
                  className="article"
                  html={htmlPaypal}
                />
              )}
            </div>
          </div>
        </DialogHotspotProductWrapper>
      )}

      {visibleDialogGalleries && (
        <Fancybox
          galleries={product.gallery || []}
          startIndex={initSlideDialogGalleries}
          onClose={onCloseDialogGalleries}
        />
      )}
    </DialogContainer>
  );
};

export default memo(DialogProduct);
